import React, { PureComponent } from 'react'
import styled from 'styled-components/native'
import { View, Platform } from 'react-native'
import { Text, TSpan, Line } from 'react-native-svg'
import { StackedBarChart, Grid } from 'react-native-svg-charts'

import { thousandSeparator } from '../../../../lib/formatters'
import { translate } from '../../../../services/translate'
import theme from '../../../../styles/theme'

const Container = styled(View)`
  flex: 1;
  justify-content: flex-end;
  height: ${props => props.height}px;
`

const BandWidthFactor = 0.4

export default class _StackedBarChart extends PureComponent {
  render () {
    let {
      colours,
      data,
      height = 200,
      width,
      maxValue,
      numberOfTicks
    } = this.props

    width = width || height
    const colors = []
    const keys = []
    colours.forEach(element => {
      colors.push(element.colour)
      keys.push(element.key)
    })

    const Labels = ({ x, y, data, width }) => {
      const yOffset = 10000
      const cash = data[0].cash
      const cashThreshold = 0.15
      const allOtherAssets = data[0].allOtherAssets
      const wholesaleFunding = data[1].wholesaleFunding
      const wsfThreshold = 0.2
      const shareholdersFunds = data[1].shareholdersFunds
      const shThreshold = 0.2
      const deposits = data[1].deposits

      const cashAsPercentageOfTotalAssets = cash / (cash + allOtherAssets)
      const wholesaleFundingAsPercentageOfTotalAssets = wholesaleFunding / (cash + allOtherAssets)
      const shfAsPercentageOfTotalAssets = shareholdersFunds / (cash + allOtherAssets)

      const wsfY = wholesaleFundingAsPercentageOfTotalAssets < wsfThreshold / 3
        ? wholesaleFunding + deposits + shareholdersFunds + (1 * yOffset)
        : wholesaleFundingAsPercentageOfTotalAssets < wsfThreshold
          ? (wholesaleFunding / 2) + deposits + shareholdersFunds
          : (wholesaleFunding / 2) + deposits + shareholdersFunds + yOffset
      return [(
        <Text
          key='cash'
          x={x(0) + (width / data.length * (1 - BandWidthFactor)) / 2}
          y={y(cashAsPercentageOfTotalAssets < cashThreshold / 2 ? cash + (1 * yOffset) : cash / 2)}
          fontSize={14}
          fontFamily={Platform.select({ web: 'ubuntu-medium' })}
          fill='black'
          alignmentBaseline='middle'
          textAnchor='middle'
        >
          {cashAsPercentageOfTotalAssets >= cashThreshold && <TSpan>{translate('treasury.screen1.chartCash', 'Cash')}</TSpan>}
          <TSpan x={x(0) + (width / data.length * (1 - BandWidthFactor)) / 2} dy={18 * (cashAsPercentageOfTotalAssets > cashThreshold)}>{(cashAsPercentageOfTotalAssets < cashThreshold ? 'Cash ' : '') + thousandSeparator(cash)}</TSpan>
        </Text>
      ), (
        <Text
          key='allOtherAssets'
          x={x(0) + (width / data.length * (1 - BandWidthFactor)) / 2}
          y={y((allOtherAssets / 2) + cash)}
          fontSize={14}
          fontFamily={Platform.select({ web: 'ubuntu-medium' })}
          fill='black'
          alignmentBaseline='middle'
          textAnchor='middle'
        >
          <TSpan>{translate('treasury.screen1.chartAssets', 'All Other Assets')}</TSpan>
          <TSpan x={x(0) + (width / data.length * (1 - BandWidthFactor)) / 2} dy='18'>{thousandSeparator(allOtherAssets)}</TSpan>
        </Text>
      ), (
        <Text
          key='wholesaleFunding'
          x={x(1) + (width / data.length * (1 - BandWidthFactor)) / 2}
          y={y(wsfY)}
          fontSize={14}
          fontFamily={Platform.select({ web: 'ubuntu-medium' })}
          fill='black'
          alignmentBaseline='middle'
          textAnchor='middle'
        >
          {wholesaleFundingAsPercentageOfTotalAssets >= wsfThreshold && <TSpan>Wholesale</TSpan>}
          {wholesaleFundingAsPercentageOfTotalAssets >= wsfThreshold && <TSpan x={x(1) + (width / data.length * (1 - BandWidthFactor)) / 2} dy='18'>Funding</TSpan>}
          <TSpan x={x(1) + (width / data.length * (1 - BandWidthFactor)) / 2} dy={18 * (wholesaleFundingAsPercentageOfTotalAssets >= wsfThreshold)}>{(wholesaleFundingAsPercentageOfTotalAssets < wsfThreshold ? 'WS Funding ' : '') + thousandSeparator(wholesaleFunding)}</TSpan>
        </Text>
      ), (
        <Text
          key='deposits'
          x={x(1) + (width / data.length * (1 - BandWidthFactor)) / 2}
          y={y((deposits / 2) + shareholdersFunds)}
          fontSize={14}
          fontFamily={Platform.select({ web: 'ubuntu-medium' })}
          fill='black'
          alignmentBaseline='middle'
          textAnchor='middle'
        >
          <TSpan>{translate('treasury.screen1.chartDeposits', 'Deposits')}</TSpan>
          <TSpan x={x(1) + (width / data.length * (1 - BandWidthFactor)) / 2} dy='18'>{thousandSeparator(deposits)}</TSpan>
        </Text>
      ), (
        <Text
          key='shareholdersFunds'
          x={x(1) + (width / data.length * (1 - BandWidthFactor)) / 2}
          y={y(shareholdersFunds / 2)}
          fontSize={14}
          fontFamily={Platform.select({ web: 'ubuntu-medium' })}
          fill='black'
          alignmentBaseline='middle'
          textAnchor='middle'
        >
          {shfAsPercentageOfTotalAssets >= shThreshold && <TSpan>Shareholders'</TSpan>}
          {shfAsPercentageOfTotalAssets >= shThreshold && <TSpan x={x(1) + (width / data.length * (1 - BandWidthFactor)) / 2} dy='18'>Funds</TSpan>}
          <TSpan x={x(1) + (width / data.length * (1 - BandWidthFactor)) / 2} dy={18 * (shfAsPercentageOfTotalAssets > shThreshold)}>{(shfAsPercentageOfTotalAssets < shThreshold ? 'SH Funds ' : '') + thousandSeparator(shareholdersFunds)}</TSpan>
        </Text>
      )]
    }

    const DottedLine = ({ x, y, data }) => {
      const maxY = data[0].allOtherAssets + data[0].cash
      return (
        <Line
          key='max-value'
          x1='0%'
          x2='100%'
          y1={y(maxY)}
          y2={y(maxY)}
          stroke={theme.colours.orange}
          strokeDasharray={[4, 8]}
          strokeWidth={2}
        />
      )
    }

    return (
      <Container height={height}>
        <StackedBarChart
          style={{ height: height, width }}
          keys={keys}
          colors={colors}
          data={data}
          showGrid
          numberOfTicks={numberOfTicks}
          spacingOuter={0.2}
          spacingInner={BandWidthFactor}
          yMin={0}
          yMax={maxValue + 50000}
          contentInset={{ top: 30, bottom: 0 }}
        >
          <Grid svg={{
            x: 0,
            stroke: '#eee'
          }}
          />
          <DottedLine />
          <Labels />
        </StackedBarChart>
        {/* <XAxis
          style={{ marginTop: 10, height: 20, width }}
          data={ xlabels }
          formatLabel={ (value, index) => data[value].label}
          contentInset={{ left: inset, right: inset }}
          svg={{ fontFamily: 'ubuntu-medium', fontSize: 14, fill: '#888888' }}
        /> */}
        {/* <YAxis
          style={{ position: 'absolute', top: -30, left: -80, height, width: 200, zIndex: 200 }}
          data={[0, maxValue]}
          min={0}
          max={maxValue}
          numberOfTicks={numberOfTicks}
          formatLabel={ (value, index) => value}
          contentInset={{ top: 10, bottom: 10 }}
          svg={{ fontFamily: 'ubuntu-medium', fontSize: 14, fill: '#888888' }}
        /> */}
      </Container>
    )
  }
}
